import React, { useState } from "react";
import { toast } from "react-toastify";
import { IoAdd, IoClose } from "react-icons/io5";
import { renderCurrencyIconColored } from "../../../../../utils/CurrencyUtils";
import SupplyApplyPills from "../../../../SupplyApplyPills";
import { uploadBOQImage, deleteFile } from "../../../../../services/budgetService";
import {
  getBoqDisplayImage,
  isPlaceholderBoqImage,
} from "../../../../../constants/boq";

const formatAed = (val) => {
  if (val == null || val === "") return null;
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString("en-US");
};

const CurrencyValue = ({ amount, dashOnZero = false }) => {
  const isEmpty = amount == null || amount === "";
  const isZero = !isEmpty && Number(amount) === 0;
  if (isEmpty || (dashOnZero && isZero)) return "-";
  const formatted = formatAed(amount);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
      }}
    >
      {renderCurrencyIconColored("black-small")}
      {formatted}
    </span>
  );
};

export const BOQItem = ({
  boq,
  showCostFields = true,
  onEdit,
  onDelete,
  onBudgetUpdated,
  disabled = false,
  isDisable = false,
  isUncovered = false,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Local mirror so the cell can swap to the freshly-uploaded image
  // without waiting for the parent's refetch to land. Placeholder
  // (id:0 + cms URL) IS rendered; only a missing/empty array shows +.
  const [imageObj, setImageObj] = useState(() => getBoqDisplayImage(boq));
  React.useEffect(() => {
    setImageObj(getBoqDisplayImage(boq));
  }, [boq.image]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hovered, setHovered] = useState(false);
  const editingDisabled = disabled || isDisable;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !boq?.id) {
      if (e.target) e.target.value = "";
      return;
    }
    try {
      setUploading(true);
      const data = await uploadBOQImage(boq.id, file);
      const images = data?.images ?? data?.data ?? [];
      const first = images[0];
      const url = first?.image ?? first?.path;
      if (data?.success !== false && url) {
        toast.success("Image uploaded successfully");
        setImageObj({ id: first?.id ?? imageObj?.id ?? null, image: url });
        onBudgetUpdated?.();
      } else {
        toast.error("Failed to upload image");
      }
    } catch (err) {
      console.error("Error uploading BOQ image:", err);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleDelete = async (e) => {
    e?.stopPropagation?.();
    if (!imageObj) return;
    // Placeholder (id:0) isn't a real file — clear locally only.
    if (isPlaceholderBoqImage(imageObj)) {
      setImageObj(null);
      onBudgetUpdated?.();
      return;
    }
    if (imageObj.id == null) return;
    try {
      setDeleting(true);
      const data = await deleteFile(imageObj.id);
      if (data?.success !== false) {
        toast.success("Image deleted successfully");
        setImageObj(null);
        onBudgetUpdated?.();
      } else {
        toast.error("Failed to delete image");
      }
    } catch (err) {
      console.error("Error deleting BOQ image:", err);
      toast.error("Failed to delete image");
    } finally {
      setDeleting(false);
    }
  };

  const leadTimeText =
    boq.lead_time != null && String(boq.lead_time).trim() !== ""
      ? String(boq.lead_time).trim()
      : "";

  const specText =
    boq.specification != null && String(boq.specification).trim() !== ""
      ? String(boq.specification).trim()
      : "";

  // Removed red border logic - always use default gray border
  // const unitCostDisplay =
  //   boq.unit_cost != null && boq.unit_cost !== ""
  //     ? formatAed(boq.unit_cost)
  //     : boq.manpower_rate != null && boq.manpower_rate !== ""
  //     ? formatAed(boq.manpower_rate)
  //     : null;
  // const isPriceMissing = showCostFields && unitCostDisplay == null;
  // const borderColor = isUncovered
  //   ? "2px solid #f87171"
  //   : isPriceMissing
  //   ? "1px solid #EF4444"
  //   : "1px solid #e5e7eb";
  const borderColor = "1px solid #e5e7eb";

  return (
    <div
      style={{
        position: "relative",
        backgroundColor: "#fff",
        borderRadius: 8,
        border: borderColor,
        padding: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          minWidth: 0,
          flexDirection: "row",
          alignItems: "stretch",
          gap: 16,
          fontSize: 12,
        }}
      >
        {/* IMAGE — view / upload / delete inline (mirrors portal BOQItem) */}
        <div style={{ width: 88, flexShrink: 0, alignSelf: "stretch" }}>
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            style={{ display: "none" }}
            id={`boq-img-up-${boq.id}`}
            disabled={uploading || editingDisabled}
          />
          <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
              position: "relative",
              height: "100%",
              minHeight: 112,
              maxHeight: 112,
              width: "100%",
              overflow: "hidden",
              borderRadius: 8,
              backgroundColor: "#e5e7eb",
            }}
          >
            {imageObj?.image ? (
              <>
                <img
                  src={imageObj.image}
                  alt={boq.item_name}
                  style={{
                    height: "100%",
                    width: "100%",
                    minHeight: 112,
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                  onClick={() => setLightboxOpen(true)}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                {/* Hover-only delete — uniform for real images and the
                    backend placeholder (id:0 is cleared locally only). */}
                {!editingDisabled && hovered && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    title="Delete image"
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      padding: 3,
                      background: "#ef4444",
                      color: "#fff",
                      border: "2px solid #fff",
                      borderRadius: 999,
                      cursor: deleting ? "not-allowed" : "pointer",
                      opacity: deleting ? 0.5 : 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                  >
                    <IoClose style={{ width: 12, height: 12 }} />
                  </button>
                )}
                {lightboxOpen && (
                  <div
                    onClick={() => setLightboxOpen(false)}
                    style={{
                      position: "fixed",
                      inset: 0,
                      backgroundColor: "rgba(0,0,0,0.8)",
                      zIndex: 9999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={imageObj.image}
                      alt=""
                      style={{ maxWidth: "90%", maxHeight: "90%" }}
                    />
                  </div>
                )}
              </>
            ) : (
              <label
                htmlFor={`boq-img-up-${boq.id}`}
                style={{
                  display: "flex",
                  height: "100%",
                  minHeight: 112,
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: editingDisabled || uploading ? "not-allowed" : "pointer",
                  opacity: uploading ? 0.6 : 1,
                }}
                title={editingDisabled ? undefined : "Upload image"}
              >
                <IoAdd style={{ width: 22, height: 22, color: "#6b7280" }} />
              </label>
            )}
          </div>
        </div>

        {/* MIDDLE: 3 rows */}
        <div style={{ display: "flex", minWidth: 0, flex: 1, flexDirection: "column", gap: 10 }}>
          {/* Row 1 */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            <div style={{ minWidth: 0, paddingRight: 4 }}>
              <div
                style={{
                  fontWeight: 500,
                  color: "#111827",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                }}
                title={boq.item_name}
              >
                {boq.item_name}
              </div>
              {boq.supply_type && (
                <div style={{ marginTop: 4 }}>
                  <SupplyApplyPills supplyType={boq.supply_type} />
                </div>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280" }}>Material Unit</div>
              <div style={{ fontWeight: 500 }}>{boq.unit_of_measurement || "-"}</div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280", marginBottom: 2 }}>BOQ Type</div>
              <span
                style={{
                  fontSize: 12,
                  color: "#1e40af",
                  backgroundColor: "#dbeafe",
                  padding: "2px 6px",
                  borderRadius: 4,
                }}
              >
                {boq.type || "Materials"}
              </span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280" }}>
                {boq.type === "Materials" ? "Quantity" : "Days"}
              </div>
              <div style={{ fontWeight: 500 }}>
                {boq.type === "Materials" ? boq.quantity || "-" : boq.days || "-"}
              </div>
            </div>
          </div>

          {/* Row 2 — Unit Cost | Total Cost | Lead Time | (spare) */}
          {showCostFields && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#6b7280" }}>
                  {boq.type === "Activities" ? "Manpower Rate" : "Unit Cost"}
                </div>
                <div style={{ fontWeight: 500 }}>
                  <CurrencyValue amount={boq.unit_cost ?? boq.manpower_rate} />
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#6b7280" }}>Total Cost</div>
                <div style={{ fontWeight: 500 }}>
                  <CurrencyValue amount={boq.total_cost} dashOnZero />
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#6b7280" }}>Lead Time</div>
                <div style={{ fontWeight: 500 }} title={leadTimeText || undefined}>
                  {leadTimeText
                    ? `${leadTimeText} day${leadTimeText === "1" ? "" : "s"}`
                    : "-"}
                </div>
              </div>
            </div>
          )}

          {/* Row 3 — specification */}
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: "#6b7280" }}>Specification</span>
            <p
              style={{
                margin: 0,
                marginTop: 2,
                fontSize: 12,
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={specText || undefined}
            >
              {specText || "—"}
            </p>
          </div>
        </div>

        {/* ACTIONS - Edit/Delete buttons commented out */}
        {/* {!isDisable && (onEdit || onDelete) && (
          <div
            style={{
              display: "flex",
              width: 110,
              flexShrink: 0,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              alignSelf: "stretch",
            }}
          >
            {onEdit && (
              <button
                onClick={() => !disabled && onEdit(boq)}
                disabled={disabled}
                style={{
                  padding: "4px 8px",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#374151",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  borderRadius: 8,
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                Edit
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => !disabled && onDelete(boq)}
                disabled={disabled}
                style={{
                  padding: 4,
                  color: "#fff",
                  backgroundColor: "#ef4444",
                  border: "none",
                  borderRadius: 8,
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IoClose style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
        )} */}
      </div>
    </div>
  );
};

export default BOQItem;
